import { useMemo } from "react";
import { CalendarDate } from "@internationalized/date";
import { usePricingPlanFeaturesQuery, ActiveCompanyFragment } from "../graphql.generated";
import { PricingFeatures } from "../pricingFeatures";
import { FeatureFlag, isPosthogFeatureFlagEnabled } from "lib/analytics/featureFlags";
/**
 * Fetches and manages pricing plan features for the active company.
 *
 * This hook queries the features enabled for the company's pricing plan
 * and provides derived data like the enabled features map and expense exceeded date.
 *
 * @param company - The detailed active company object (`ActiveCompanyFragment`) or undefined.
 *                  Used to get the company ID and determine if the query should be skipped.
 * @returns An object containing:
 *  - `pricePlanFeatureEnabled`: A memoized Map where keys are `PricingFeatures` and values are boolean (true if enabled).
 *  - `pricingPlanFeaturesLoading`: Boolean loading state for the pricing features query.
 *  - `refetchPricingPlanFeatures`: Function to manually refetch the pricing features.
 *  - `expenseExceededDate`: The `CalendarDate` when expenses exceeded limits, or undefined.
 */
export const useCompanyPricingFeatures = (company: ActiveCompanyFragment | undefined) => {
  const companyId = company?.id;
  const shouldSkip = !company; // Skip if no company object is provided

  if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging))
    console.log("useCompanyPricingFeatures: Hook executing.", { companyId, shouldSkip });

  // --- Data Fetching ---

  // Query for the pricing plan features for the given companyId.
  // Skip the query if no company is active.
  const {
    data: pricingPlanFeaturesData,
    loading: pricingPlanFeaturesLoading,
    refetch: refetchPricingPlanFeatures,
    error,
  } = usePricingPlanFeaturesQuery({
    skip: shouldSkip,
    variables: { companyId: companyId ?? "" }, // Provide companyId, default to empty string if undefined
    fetchPolicy: "cache-and-network",
  });
  if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging))
    console.log("useCompanyPricingFeatures: usePricingPlanFeaturesQuery result", {
      loading: pricingPlanFeaturesLoading,
      error,
      data: pricingPlanFeaturesData,
    });

  // --- Memoized Data ---

  // This map provides quick lookups to check if a specific feature is enabled.
  const pricePlanFeatureEnabled = useMemo(() => {
    if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging))
      console.log("useCompanyPricingFeatures: Recalculating pricePlanFeatureEnabled map.");
    const newMap = new Map<PricingFeatures, boolean>();
    // Safely access the feature names array.
    const featureNames = pricingPlanFeaturesData?.pricingPlanFeatures?.featureNames;
    if (featureNames) {
      // Iterate through the names provided by the query result.
      featureNames.forEach((name) => {
        // Assume any feature name present in the list is enabled.
        newMap.set(name as PricingFeatures, true);
      });
    }
    if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging))
      console.log("useCompanyPricingFeatures: pricePlanFeatureEnabled map created:", newMap);
    return newMap;
  }, [pricingPlanFeaturesData]);

  // Extract the expense exceeded date directly from the data.
  const expenseExceededDate = useMemo(() => {
    const date = pricingPlanFeaturesData?.pricingPlanFeatures?.expenseExceededDate as
      | CalendarDate
      | undefined;
    if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging))
      console.log("useCompanyPricingFeatures: Extracted expenseExceededDate:", date);
    return date;
  }, [pricingPlanFeaturesData?.pricingPlanFeatures?.expenseExceededDate]);

  // --- Return Value ---
  /*
    Returns an object containing:
    - pricePlanFeatureEnabled: A memoized Map where keys are `PricingFeatures` and values are boolean (true if enabled).
    - pricingPlanFeaturesLoading: Boolean loading state for the pricing features query.
    - refetchPricingPlanFeatures: Function to manually refetch the pricing features.
    - expenseExceededDate: The `CalendarDate` when expenses exceeded limits, or undefined.
  */

  // Return the processed data, loading state, and refetch function.
  const result = {
    pricePlanFeatureEnabled,
    pricingPlanFeaturesLoading,
    refetchPricingPlanFeatures,
    expenseExceededDate,
  };

  if (isPosthogFeatureFlagEnabled(FeatureFlag.ActiveCompanyLogging))
    console.log("useCompanyPricingFeatures: Returning", {
      mapKeys: Array.from(result.pricePlanFeatureEnabled.keys()),
      loading: result.pricingPlanFeaturesLoading,
      expenseExceededDate: result.expenseExceededDate,
      hasRefetch: !!result.refetchPricingPlanFeatures,
    });
  return result;
};
